import { CustomInnerHeaderGroupProps } from "ag-grid-react";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router";
import { Box, Divider, IconButton, Menu, MenuItem } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";

import { ScenarioModelGridContext } from "../../../../types/scenarioModelGridContext";
import { SCENARIO_MODEL_MODE, ScenarioModelModeType } from "../../constants";

export const ScenarioInnerGroupHeader: React.FC<CustomInnerHeaderGroupProps> = (
  props,
) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { onScenarioAction } = props.context as ScenarioModelGridContext;

  const navigate = useNavigate();
  const scenarioId = props.columnGroup.getProvidedColumnGroup().getColGroupDef()
    ?.headerGroupComponentParams?.scenarioId as string;
  const isBaseline = props.columnGroup.getProvidedColumnGroup().getColGroupDef()
    ?.headerGroupComponentParams?.isBaseline as boolean;
  const modelId = props.columnGroup.getProvidedColumnGroup().getColGroupDef()
    ?.headerGroupComponentParams?.modelId as string;

  const handleViewEdit = (): void => {
    setAnchorEl(null);
    navigate(`${modelId}/${scenarioId}`);
  };

  const onClick = useCallback(
    (modalType: ScenarioModelModeType, name?: string) => {
      setAnchorEl(null);
      onScenarioAction(modalType, scenarioId, name);
    },
    [onScenarioAction, scenarioId],
  );

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
      }}
    >
      <span>{props.displayName}</span>
      <IconButton
        size="small"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{ p: 0.25 }}
        aria-label="Scenario actions"
        aria-haspopup="menu"
        aria-expanded={anchorEl ? "true" : undefined}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        slotProps={{ list: { dense: true } }}
      >
        {isBaseline ? (
          <BaselineScenarioMenuActions
            name={props.displayName}
            onClick={onClick}
          />
        ) : (
          <OtherScenarioMenuActions
            name={props.displayName}
            onClick={onClick}
            handleViewEdit={handleViewEdit}
          />
        )}
      </Menu>
    </Box>
  );
};

type OtherScenarioMenuActionsProps = {
  handleViewEdit: () => void;
  name: string;
  onClick: (modelMode: ScenarioModelModeType, value?: string) => void;
};

const OtherScenarioMenuActions: React.FC<OtherScenarioMenuActionsProps> = ({
  handleViewEdit,
  name,
  onClick,
}) => (
  <>
    <MenuItem onClick={handleViewEdit}>View/Edit</MenuItem>
    <Divider />
    <MenuItem
      onClick={() => onClick(SCENARIO_MODEL_MODE.Duplicate, `${name} (Copy)`)}
    >
      Duplicate
    </MenuItem>
    <MenuItem
      onClick={() => onClick(SCENARIO_MODEL_MODE.RenameScenarioName, name)}
    >
      Rename
    </MenuItem>
    <MenuItem onClick={() => onClick(SCENARIO_MODEL_MODE.Remove, name)}>
      Remove
    </MenuItem>
  </>
);

const BaselineScenarioMenuActions: React.FC<
  Omit<OtherScenarioMenuActionsProps, "handleViewEdit">
> = ({ onClick }) => (
  <>
    <MenuItem onClick={() => onClick(SCENARIO_MODEL_MODE.Add, "Scenario A")}>
      Add Scenario
    </MenuItem>
  </>
);
